import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtFecha } from "../../lib/format";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

const send = async (fn, ok, done) => {
  try {
    await fn();
    toast.success(ok);
    done();
  } catch (e) {
    toast.error(errMsg(e));
  }
};

const METODOS = [["presencial_con_documento", "Presencial, con su documento"], ["videollamada_con_documento", "Videollamada, mostrando su documento"], ["correo_registrado_en_ficha", "Desde el correo registrado en su ficha"], ["telefono_registrado_en_ficha", "Llamada al teléfono registrado en su ficha"], ["no_verificada_se_rechaza", "No se pudo verificar (solo rechazar)"]];

const Solicitud = ({ s, onDone }) => {
  const [r, setR] = useState("");
  const [met, setMet] = useState("");
  const [det, setDet] = useState("");
  const ok = r.trim().length >= 10 && met && det.trim().length >= 10;
  return (
    <div className="border border-slate-200 rounded-lg p-3 text-sm space-y-1" data-testid={`titular-${s.id}`}>
      <p><b>{s.tipo}</b> · {s.nombre} · {s.documento} · {s.correo} · {fmtFecha(s.fecha)} · <b className={s.dias_restantes < 5 ? "text-rose-700" : ""}>{s.dias_restantes} días restantes</b> · {s.estado}</p>
      <p className="text-slate-600">{s.detalle}</p>
      {s.constancia_conservacion && <p className="bg-amber-50 text-amber-800 rounded px-2 py-1" data-testid="titular-constancia">Constancia: {s.constancia_conservacion.texto}{s.constancia_conservacion.relacion_vigente ? " (relación vigente)" : ""}.</p>}
      {s.identidad_verificada && <p className="text-xs">Identidad verificada: {s.identidad_verificada.metodo} · {s.identidad_verificada.detalle} · {s.identidad_verificada.por}</p>}
      {s.respuesta ? <p>Respuesta: {s.respuesta.texto} · {s.respuesta.por}</p> : (
        <div className="space-y-1">
          <p className="text-xs text-amber-700">El sistema no entrega datos automáticamente. Verifica la identidad de quien pide antes de responder.</p>
          <select className="h-9 border rounded-md px-2 text-sm" value={met} onChange={(e) => setMet(e.target.value)} data-testid={`titular-metodo-${s.id}`}>
            <option value="">¿Cómo verificaste su identidad?</option>
            {METODOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <Textarea placeholder="Detalle de la verificación (mínimo 10 caracteres)" value={det} onChange={(e) => setDet(e.target.value)} data-testid={`titular-verif-${s.id}`} />
          <Textarea placeholder="Respuesta (mínimo 10 caracteres)" value={r} onChange={(e) => setR(e.target.value)} data-testid={`titular-respuesta-${s.id}`} />
          {["respondida", "respondida_parcial_conservacion", "rechazada"].map((e) => (
            <Button key={e} size="sm" variant="outline" className="mr-2" disabled={!ok}
              onClick={() => send(() => api.post(`/company/solicitudes-titular/${s.id}/responder`, { respuesta: r, estado: e, verificacion_metodo: met, verificacion_detalle: det }), "Respondida", onDone)} data-testid={`titular-${e}-${s.id}`}>{e}</Button>
          ))}
        </div>
      )}
    </div>
  );
};

const NuevaSolicitud = ({ onDone }) => {
  const [f, setF] = useState({ tipo: "acceso", nombre: "", documento: "", correo: "", detalle: "" });
  return (
    <div className="flex flex-wrap gap-2" data-testid="titular-form">
      <select className="h-9 border rounded-md px-2 text-sm" value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })} data-testid="titular-tipo">
        {["acceso", "rectificacion", "supresion", "oposicion"].map((t) => <option key={t}>{t}</option>)}
      </select>
      {["nombre", "documento", "correo", "detalle"].map((k) => <Input key={k} className="w-44 h-9" placeholder={k} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`titular-${k}`} />)}
      <Button size="sm" onClick={() => send(() => api.post("/company/solicitudes-titular", f), "Solicitud registrada", onDone)} data-testid="titular-submit">Registrar solicitud</Button>
    </div>
  );
};

const Brechas = () => {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ fecha_incidente: "", descripcion: "", datos_afectados: "", avisados: "", medidas: "" });
  const load = useCallback(() => {
    api.get("/company/brechas").then((r) => setItems(r.data.items));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  return (
    <div className="space-y-3" data-testid="breaches">
      <Input type="date" className="w-44" value={f.fecha_incidente} onChange={(e) => setF({ ...f, fecha_incidente: e.target.value })} data-testid="breach-date" />
      {[["descripcion", "Qué pasó"], ["datos_afectados", "Datos afectados"], ["avisados", "A quién se avisó (titulares, autoridad) y cuándo"], ["medidas", "Medidas tomadas"]].map(([k, l]) => (
        <Textarea key={k} placeholder={l} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`breach-${k}`} />
      ))}
      <Button onClick={() => send(() => api.post("/company/brechas", f), "Incidente registrado", load)} data-testid="breach-submit">Registrar incidente</Button>
      {items.map((b) => <p key={b.id} className="text-sm border-b pb-1">{b.fecha_incidente} · {b.descripcion} · afectados: {b.datos_afectados} · avisados: {b.avisados} · {b.registrado_por}</p>)}
    </div>
  );
};

export default function PrivacidadPanel() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    api.get("/company/solicitudes-titular").then((r) => setItems(r.data.items));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  return (
    <div className="space-y-6">
      <PageHeader overline="Ley 21.719" title="Privacidad" />
      <Tabs defaultValue="solicitudes">
        <TabsList><TabsTrigger value="solicitudes" data-testid="tab-titular">Solicitudes del titular</TabsTrigger><TabsTrigger value="brechas" data-testid="tab-brechas">Registro de brechas</TabsTrigger></TabsList>
        <TabsContent value="solicitudes" className="space-y-3">
          <NuevaSolicitud onDone={load} />
          {items.map((s) => <Solicitud key={s.id} s={s} onDone={load} />)}
        </TabsContent>
        <TabsContent value="brechas"><Brechas /></TabsContent>
      </Tabs>
    </div>
  );
}
